/**
 * BE error-code → i18n key map.
 *
 * The BE wire shape is `{ error: { code, message, details? } }` per
 * `3mote-be-new/src/lib/errors.ts`. The `code` is canonical and stable;
 * `message` is an English dev fallback. The FE prefers a localized
 * render via this map and only falls back to `message` for codes we
 * haven't mapped yet (defense-in-depth — a fresh BE code shipping ahead
 * of the FE catalog should still produce a readable toast).
 *
 * Adding a new error code:
 *   1. Add it to the FE `errors.json` catalog (en + es) under the right
 *      bucket (`generic` for AppError taxonomy, `domain` for
 *      domain-specific codes).
 *   2. Map it here `code → i18n key`.
 *
 * The `validation_error` code is special: the BE attaches
 * `details.fields: string[]` (Zod field paths) so the FE can render
 * per-field messages via `<FormField>`'s `role="alert"` surface. The
 * generic key is the toast/banner fallback when no field UI is
 * present.
 */

import type { TFunction } from 'i18next'

import { ApiError } from './client'

export interface BeErrorEnvelope {
  code: string
  message: string
  details?: {
    fields?: string[]
    issues?: Array<{ path: string[]; code: string; message: string }>
    /**
     * Domain codes may attach extra context for the FE to interpolate
     * into the localized message (e.g. `last_status_of_type` carries the
     * offending `columnType` slug). Open-ended on purpose — a new code
     * shipping extra detail must not require widening this type first.
     */
    [key: string]: unknown
  }
}

/**
 * Generic AppError taxonomy (1:1 with `AppErrorCode` in
 * `3mote-be-new/src/lib/errors.ts`). Exported for the catalog-drift
 * test (`__tests__/error-codes.test.ts`) — runtime callers go through
 * `localizeError` instead.
 */
export const GENERIC_KEYS: Readonly<Record<string, string>> = {
  unauthorized: 'errors:generic.unauthorized',
  forbidden: 'errors:generic.forbidden',
  not_found: 'errors:generic.not_found',
  bad_request: 'errors:generic.bad_request',
  conflict: 'errors:generic.conflict',
  internal: 'errors:generic.internal',
  validation_error: 'errors:generic.validation_error',
}

/**
 * Domain-specific codes the BE emits — every entry here MUST also exist
 * in `errors.json` under `domain.<code>`. Phase 2 seeds the canonical
 * codes; adding a new domain-error in a BE feature ships a paired entry
 * in the FE catalog (data/logic reviewer rule).
 *
 * **Code casing matches the BE wire shape exactly.** The board code
 * `CARD_NOT_MOVABLE` is uppercase per `src/board/move-route.ts` (which
 * emits `code: 'CARD_NOT_MOVABLE'` directly via a custom envelope).
 * Chat codes are lowercase_snake_case per
 * `3mote-be-new/src/chat/chat-error-codes.ts`.
 *
 * **Domain codes ride on the envelope's `code` field directly** — the
 * chat services throw via `new <AppError>(message, CHAT_ERROR_CODES.X)`
 * so the wire envelope `{ error: { code: 'cannot_dm_self', message: ... } }`
 * surfaces the domain code on the standard `AppError` throw path. The
 * route-helper short-circuit `chatErrorResponse()` is still used for a
 * few paths (e.g. decryption-failed) that need to bypass the standard
 * throw chain — the map below applies to BOTH paths since the wire shape
 * is identical.
 *
 * `EVENT_COLUMN_LOCK` is intentionally NOT in this map — the BE throws
 * `BadRequestError(EVENT_COLUMN_LOCK_MESSAGE)` whose wire `code` is
 * `'bad_request'`, not the domain code. The English fallback message
 * carries through.
 */
export const DOMAIN_KEYS: Readonly<Record<string, string>> = {
  // src/board/move-route.ts — emitted as code via custom envelope
  CARD_NOT_MOVABLE: 'errors:domain.CARD_NOT_MOVABLE',
  // src/lib/errors.ts BOARD_ERROR_CODES
  card_shorthand_ambiguous: 'errors:domain.card_shorthand_ambiguous',
  // src/lib/errors.ts ROLE_ERROR_CODES — interpolates `details.slugs`
  role_permission_not_held: 'errors:domain.role_permission_not_held',
  // src/lib/errors.ts TEAM_ERROR_CODES
  team_name_taken: 'errors:domain.team_name_taken',
  // src/chat/chat-error-codes.ts
  channel_archived: 'errors:domain.channel_archived',
  channel_name_taken: 'errors:domain.channel_name_taken',
  message_not_author: 'errors:domain.message_not_author',
  not_participant: 'errors:domain.not_participant',
  cannot_dm_self: 'errors:domain.cannot_dm_self',
  cannot_remove_creator: 'errors:domain.cannot_remove_creator',
  thread_too_deep: 'errors:domain.thread_too_deep',
  parent_message_not_found: 'errors:domain.parent_message_not_found',
  parent_in_different_conversation: 'errors:domain.parent_in_different_conversation',
  cannot_reply_to_deleted: 'errors:domain.cannot_reply_to_deleted',
  cannot_reply_to_system: 'errors:domain.cannot_reply_to_system',
  decryption_failed: 'errors:domain.decryption_failed',
  not_yet_implemented: 'errors:domain.not_yet_implemented',
  invalid_cursor: 'errors:domain.invalid_cursor',
  emoji_empty: 'errors:domain.emoji_empty',
  emoji_too_long: 'errors:domain.emoji_too_long',
  message_not_found: 'errors:domain.message_not_found',
  conversation_not_found: 'errors:domain.conversation_not_found',
  // Chat attachments (v4). Body+attachments parity, ownership
  // check, two-phase attach lifecycle, count cap. All emitted from
  // `3mote-be-new/src/chat/chat-error-codes.ts` on the send-message and
  // attach paths.
  message_empty: 'errors:domain.message_empty',
  attachment_not_found: 'errors:domain.attachment_not_found',
  attachment_not_pending: 'errors:domain.attachment_not_pending',
  attachment_not_author: 'errors:domain.attachment_not_author',
  too_many_attachments: 'errors:domain.too_many_attachments',
  // A card attachment the card's saved description still shows can't be
  // removed from the Attachments section; it goes when the description
  // drops it and is saved.
  attachment_in_description: 'errors:domain.attachment_in_description',
  // Channel-wide @all mention
  all_mention_not_allowed_in_dm: 'errors:domain.all_mention_not_allowed_in_dm',
  // Wiki / Documentation feature — codes correspond to the BE error
  // taxonomy in the wiki plan §3 (publish concurrency, draft staleness,
  // soft-delete state, ACL last-owner rule, PHI gates, move depth cap).
  // Each maps to a string in errors.json under `domain.<code>` so the
  // catalog-drift test (`__tests__/error-codes.test.ts`) catches missing
  // translations on either side.
  wiki_doc_not_found: 'errors:domain.wiki_doc_not_found',
  wiki_doc_forbidden: 'errors:domain.wiki_doc_forbidden',
  wiki_doc_deleted: 'errors:domain.wiki_doc_deleted',
  wiki_publish_conflict: 'errors:domain.wiki_publish_conflict',
  wiki_publish_forbidden: 'errors:domain.wiki_publish_forbidden',
  wiki_draft_stale: 'errors:domain.wiki_draft_stale',
  wiki_idempotency_conflict: 'errors:domain.wiki_idempotency_conflict',
  wiki_acl_last_owner: 'errors:domain.wiki_acl_last_owner',
  wiki_agent_phi_blocked: 'errors:domain.wiki_agent_phi_blocked',
  wiki_phi_public_share_blocked: 'errors:domain.wiki_phi_public_share_blocked',
  wiki_move_depth_exceeded: 'errors:domain.wiki_move_depth_exceeded',
  // Phase 3a — version-history surface.
  wiki_version_not_found: 'errors:domain.wiki_version_not_found',
  wiki_version_restore_forbidden: 'errors:domain.wiki_version_restore_forbidden',
  // Inline title rename rejected for empty / whitespace-only name. The
  // FE blocks empty submissions client-side; this code is the defense-
  // in-depth signal that lets `useUpdateDoc` roll back the optimistic
  // cache write and re-show the previous title.
  wiki_node_name_invalid: 'errors:domain.wiki_node_name_invalid',
  wiki_favorites_kind_not_allowed: 'errors:domain.wiki_favorites_kind_not_allowed',
  // Phase 3b — per-doc ACL surface.
  wiki_node_not_found: 'errors:domain.wiki_node_not_found',
  wiki_acl_invalid_subject: 'errors:domain.wiki_acl_invalid_subject',
  wiki_acl_duplicate_subject: 'errors:domain.wiki_acl_duplicate_subject',
  wiki_acl_forbidden: 'errors:domain.wiki_acl_forbidden',
  // Phase 5 Wave B — Notion-style URL resolver. The pretty-URL route
  // surfaces this directly (with its own collision-aware copy) rather
  // than routing through the global toast.
  wiki_tail_collision: 'errors:domain.wiki_tail_collision',
  // Phase 5 Wave D — Drive-style file nodes. `invalid_mime` fires when
  // the BE allowlist rejects a picked file's MIME (PDF + images +
  // common docs only); `too_large` fires when sizeBytes > 100 MB.
  // `not_ready` fires on download attempts against a placeholder row
  // that hasn't completed its `POST /attach` step (PUT failed or the
  // tab closed mid-upload). All three are surfaced inline near the
  // file picker rather than as a global toast.
  wiki_file_invalid_mime: 'errors:domain.wiki_file_invalid_mime',
  wiki_file_too_large: 'errors:domain.wiki_file_too_large',
  wiki_file_node_not_ready: 'errors:domain.wiki_file_node_not_ready',
  // Fires on attach/download/delete when the target node isn't kind='file'
  // (e.g., a UUID points at a folder or doc). The user fix is "refresh"
  // because the tree's cached row may be stale.
  wiki_node_kind_mismatch: 'errors:domain.wiki_node_kind_mismatch',
  // Comments + reactions (Confluence-style page comments + per-comment
  // reactions). Mirrors the WIKI_ERROR_CODES enum in the BE — each code
  // here lands in errors.json under `domain.<code>` for localized toasts.
  wiki_comment_not_found: 'errors:domain.wiki_comment_not_found',
  wiki_comment_forbidden: 'errors:domain.wiki_comment_forbidden',
  wiki_comment_author_only: 'errors:domain.wiki_comment_author_only',
  wiki_comment_resolve_state: 'errors:domain.wiki_comment_resolve_state',
  wiki_comment_parent_invalid: 'errors:domain.wiki_comment_parent_invalid',
  wiki_comment_kind_invalid: 'errors:domain.wiki_comment_kind_invalid',
  wiki_reaction_invalid_emoji: 'errors:domain.wiki_reaction_invalid_emoji',
  // Advisory wiki-doc edit lock (interim until live co-editing). Fires
  // on lock acquire when another user holds a live lock, and on
  // save/publish from a tab that lost the lock (the BE write guard).
  // The 409 envelope carries the holder in `details.holder`; the doc
  // page's take-over flow branches on it before this generic toast.
  wiki_edit_locked: 'errors:domain.wiki_edit_locked',
  wiki_task_item_stale: 'errors:domain.wiki_task_item_stale',
  // MOTE-162 — per-tenant storage quota. `storage_quota_exceeded` (403)
  // fires at presign when committing the file would push the tenant over
  // its plan pool; `file_too_large` (400) fires when a single file
  // exceeds the plan's per-file cap. Both surface as an upload toast.
  storage_quota_exceeded: 'errors:domain.storage_quota_exceeded',
  file_too_large: 'errors:domain.file_too_large',
  // MOTE-33 — an atomic multi-file copy (wiki doc duplication) that would
  // push the tenant further past its pool than the overshoot allowance
  // permits. Separate from `storage_quota_exceeded` because the remedy
  // differs: an upload can be retried smaller, an indivisible copy can't.
  // Carries `details.{requiredBytes,availableBytes,quotaBytes}` so the
  // rejection can name real numbers; the wiki duplicate flow renders its
  // own modal from those and never reaches this generic toast key.
  storage_duplicate_quota_exceeded: 'errors:domain.storage_duplicate_quota_exceeded',
  // Attach measured the stored object and found it absent / larger than
  // declared. Both are actionable in a way a bare "Conflict" is not.
  upload_incomplete: 'errors:domain.upload_incomplete',
  upload_size_mismatch: 'errors:domain.upload_size_mismatch',
  // MOTE-162 — free-tier wiki limits. `wiki_doc_cap_exceeded` (403) on
  // create when at the plan doc cap; `wiki_text_limit_exceeded` (403) on
  // save when the body would push the tenant over the text ceiling.
  wiki_doc_cap_exceeded: 'errors:domain.wiki_doc_cap_exceeded',
  wiki_text_limit_exceeded: 'errors:domain.wiki_text_limit_exceeded',
  // ADR-039 — canonical rich-text JSON. Fires when a submitted TipTap
  // document fails the BE's per-surface schema validation (unknown
  // node/mark type, malformed mention attrs, depth/size cap). Surfaces
  // as a save-failure toast on any rich-text composer.
  richtext_invalid: 'errors:domain.richtext_invalid',
  // Typed custom columns. `custom_columns_require_paid_plan` (403) fires
  // on status create when the tenant's EFFECTIVE plan is `starter` — the
  // five seeded defaults come from project seeding, not this route, so a
  // starter tenant is unaffected in the normal case. Following the
  // "restrict, don't destroy" rule, a downgraded tenant keeps its existing
  // custom columns fully editable; only creating new ones is blocked, so
  // this never fires on a PATCH.
  //
  // `last_status_of_type` (409) fires from BOTH delete AND patch — a
  // RE-TYPE that would leave the project with no status of the vacated
  // type is refused just like a delete. That invariant is what makes every
  // type → status resolution total, so no read path ever needs a "this
  // project has no Done column" fallback. Its envelope carries
  // `details.columnType` (the type slug), which callers map to a human
  // label and pass as the `{column}` interpolation param — see
  // `describeMutationError` in `use-project-statuses.ts`.
  custom_columns_require_paid_plan: 'errors:domain.custom_columns_require_paid_plan',
  last_status_of_type: 'errors:domain.last_status_of_type',
  // Self-removal from a project is refused because it is a one-way
  // door: every route into a project needs a membership row, so
  // dropping your own closes the screen you would undo it from.
  cannot_remove_self_from_project: 'errors:domain.cannot_remove_self_from_project',
  // Google / Microsoft / Apple SSO (BE `src/auth/sso-error-codes.ts`). All
  // three are surfaced on the /login banner via `describeSsoError`, which
  // passes `{provider}` ("Google" / "Microsoft" / "Apple" — brand names,
  // never translated) as the interpolation param `sso_not_eligible`'s
  // copy consumes.
  // `sso_not_eligible` is deliberately opaque on the BE (unknown /
  // deactivated / agent / ambiguous all collapse into it — anti-
  // enumeration), so the copy tells the user the one actionable fix:
  // get invited.
  sso_token_invalid: 'errors:domain.sso_token_invalid',
  sso_not_eligible: 'errors:domain.sso_not_eligible',
  sso_bridge_failed: 'errors:domain.sso_bridge_failed',
  // `event_column_locked` (400) fires when a time-derived card (event /
  // announcement) is aimed at a column whose TYPE cannot hold one. It was
  // previously thrown with no code, so the FE rendered the BE's English
  // message verbatim — and post-typing that message cannot name a
  // project's actual columns anyway, since any of them may be renamed. The
  // catalog copy names the three column TYPES instead, which stays true.
  event_column_locked: 'errors:domain.event_column_locked',
  // Guest accounts. Guest-ness is a role slug that also acts as a hard
  // ceiling, so most of these are refusals of something the FE already
  // hides — they exist as the defence-in-depth answer when a stale tab,
  // a direct API call, or a concurrent role change reaches the route
  // anyway.
  //
  // Two carry extra context the copy deliberately does NOT interpolate:
  // `guest_limit_reached` ships `details.{limit,used}` and
  // `guest_transition_sole_project_owner` ships `details.projectIds`. A
  // missing ICU argument renders as the literal `{limit}` rather than
  // being dropped, and these codes surface from several call sites (the
  // invite modal, the Users role select, reactivate), so a token in the
  // shared catalog string would leak braces from whichever site had not
  // wired the params. Same shape as `wiki_edit_locked`, whose 409 also
  // carries a `details.holder` the generic copy ignores: the owning
  // surface reads `getErrorDetails(err)` and renders its own richer
  // sentence, and this string is the honest fallback for everyone else.
  guest_limit_reached: 'errors:domain.guest_limit_reached',
  guest_channel_invalid: 'errors:domain.guest_channel_invalid',
  guest_channel_only: 'errors:domain.guest_channel_only',
  guest_channel_not_guest: 'errors:domain.guest_channel_not_guest',
  guest_dm_not_allowed: 'errors:domain.guest_dm_not_allowed',
  guest_project_admin: 'errors:domain.guest_project_admin',
  guest_forbidden: 'errors:domain.guest_forbidden',
  guest_transition_sole_project_owner: 'errors:domain.guest_transition_sole_project_owner',
  // Refusal to edit a role whose permission set is fixed in code. Keyed
  // off the BE's locked-slug set rather than the `guest` slug itself, so
  // the copy stays true if another role is ever locked.
  role_locked: 'errors:domain.role_locked',
  // The wiki write-side guard: a guest subject may only ever hold the
  // `viewer` doc role. The share modal locks the control, so reaching
  // this means the roster was stale or the request came from elsewhere.
  wiki_guest_viewer_only: 'errors:domain.wiki_guest_viewer_only',
  // Performance reports window validation. Both are 400s the FE should
  // never provoke — the period picker refuses an inverted range before
  // it navigates and every preset is inside the span cap — so reaching
  // one means a hand-edited `?from=`/`?to=` in the URL. They surface
  // inline on the affected widget ("Adjust the period and try again")
  // rather than as a global toast, since the rest of the dashboard may
  // be rendering a perfectly valid window of its own.
  //
  // `reports_window_too_large` covers TWO distinct BE refusals — a span
  // over 731 days, and a span that is short enough but would produce
  // more than 400 buckets at the requested granularity. The copy names
  // the fix (narrow the range, or group by week or month) rather than
  // either limit, because the user cannot tell which one they hit.
  reports_invalid_range: 'errors:domain.reports_invalid_range',
  reports_window_too_large: 'errors:domain.reports_window_too_large',
  // Card activity log. `card_activity_revert_stale` (409) is the revert
  // dialog's own branch — it reads `details.current` and re-asks inline
  // rather than toasting — so this copy is only the fallback. The other
  // two toast: the entry was removed with its card, or the field is not
  // one a revert can touch (the FE hides the button, so reaching it means
  // a stale tab).
  card_activity_not_found: 'errors:domain.card_activity_not_found',
  card_activity_not_revertible: 'errors:domain.card_activity_not_revertible',
  card_activity_revert_stale: 'errors:domain.card_activity_revert_stale',
  // AI agent seats (BE `AGENT_ERROR_CODES`). Surfaced inline in the Add agent
  // and Agent settings dialogs. `agent_done_forbidden` and
  // `agent_mint_refused` are runtime refusals an admin only meets if a
  // stale tab reaches the route; they still carry copy so a toast never
  // falls back to the BE's English message.
  agent_role_forbidden: 'errors:domain.agent_role_forbidden',
  agent_done_forbidden: 'errors:domain.agent_done_forbidden',
  agent_mint_refused: 'errors:domain.agent_mint_refused',
  agent_reserved_email: 'errors:domain.agent_reserved_email',
  agent_key_rejected: 'errors:domain.agent_key_rejected',
  agent_provider_unreachable: 'errors:domain.agent_provider_unreachable',
  agent_base_url_blocked: 'errors:domain.agent_base_url_blocked',
  agent_private_endpoint_premium: 'errors:domain.agent_private_endpoint_premium',
  agent_not_found: 'errors:domain.agent_not_found',
  agent_profile_locked: 'errors:domain.agent_profile_locked',
  agent_forbidden: 'errors:domain.agent_forbidden',
  agent_key_required: 'errors:domain.agent_key_required',
  agent_accountable_invalid: 'errors:domain.agent_accountable_invalid',
  agent_model_required: 'errors:domain.agent_model_required',
  agent_conflict: 'errors:domain.agent_conflict',
  agent_avatar_invalid: 'errors:domain.agent_avatar_invalid',
  // An agent tried to publish a wiki doc; agents save drafts only.
  // Code connections (GitHub / CodeCommit), project repositories, card repository.
  code_integration_unavailable: 'errors:domain.code_integration_unavailable',
  code_connection_limit: 'errors:domain.code_connection_limit',
  code_connection_not_found: 'errors:domain.code_connection_not_found',
  code_connection_revoked: 'errors:domain.code_connection_revoked',
  code_connection_exists: 'errors:domain.code_connection_exists',
  code_connection_provider_mismatch: 'errors:domain.code_connection_provider_mismatch',
  code_region_invalid: 'errors:domain.code_region_invalid',
  code_role_arn_invalid: 'errors:domain.code_role_arn_invalid',
  code_repository_not_found: 'errors:domain.code_repository_not_found',
  code_repository_unavailable: 'errors:domain.code_repository_unavailable',
  code_repository_already_linked: 'errors:domain.code_repository_already_linked',
  card_repository_not_in_project: 'errors:domain.card_repository_not_in_project',
  card_repository_unsupported: 'errors:domain.card_repository_unsupported',
  code_connect_expired: 'errors:domain.code_connect_expired',
  code_connect_cancelled: 'errors:domain.code_connect_cancelled',
  code_installation_in_use: 'errors:domain.code_installation_in_use',
  code_installation_not_admin: 'errors:domain.code_installation_not_admin',
  code_connect_state_mismatch: 'errors:domain.code_connect_state_mismatch',
  code_connect_failed: 'errors:domain.code_connect_failed',
  code_webhook_signature_invalid: 'errors:domain.code_webhook_signature_invalid',
  code_self_account_forbidden: 'errors:domain.code_self_account_forbidden',
  agent_publish_forbidden: 'errors:domain.agent_publish_forbidden',
  // Agent turn hardening: every agent API call must name a live run, agents
  // can't @all, and per-agent hourly caps refuse runaway writes.
  agent_run_required: 'errors:domain.agent_run_required',
  agent_mention_all_forbidden: 'errors:domain.agent_mention_all_forbidden',
  agent_assignee_not_member: 'errors:domain.agent_assignee_not_member',
  agent_rate_limited: 'errors:domain.agent_rate_limited',
  // A route only an AI agent may call (its own workstation).
  agent_only: 'errors:domain.agent_only',
  // An agent asked for a workstation for someone whose role doesn't allow it.
  agent_workstation_requester_forbidden: 'errors:domain.agent_workstation_requester_forbidden',
  // Agent workstations: policy off, one live session per agent, monthly
  // hours spent, the 12 h ceiling, a failed launch, and no capacity.
  workstation_disabled: 'errors:domain.workstation_disabled',
  workstation_already_running: 'errors:domain.workstation_already_running',
  workstation_hours_exhausted: 'errors:domain.workstation_hours_exhausted',
  workstation_extend_limit: 'errors:domain.workstation_extend_limit',
  workstation_launch_failed: 'errors:domain.workstation_launch_failed',
  workstation_unavailable: 'errors:domain.workstation_unavailable',
  workstation_from_workstation: 'errors:domain.workstation_from_workstation',
  workstation_agent_deactivated: 'errors:domain.workstation_agent_deactivated',
  workstation_policy_limit: 'errors:domain.workstation_policy_limit',
  workstation_no_session: 'errors:domain.workstation_no_session',
  workstation_busy_relaunching: 'errors:domain.workstation_busy_relaunching',
  workstation_orchestrator_failed: 'errors:domain.workstation_orchestrator_failed',
  // Idempotency middleware: a replayed key whose first request is still in
  // flight (409), and a key reused with a different payload (409).
  idempotency_in_progress: 'errors:domain.idempotency_in_progress',
  idempotency_key_reused: 'errors:domain.idempotency_key_reused',
}

/**
 * Resolve the localized human-readable message for an error thrown by
 * `apiClient`. Accepts a plain `Error` so it can be called directly from
 * a TanStack Query `onError(err)` callback (where the mutation type is
 * usually `Error`, not `ApiError`) — non-`ApiError` instances skip the
 * envelope lookup and fall through to `err.message`.
 *
 * Fallback chain:
 *   1. Domain-specific code → `errors:domain.<code>`.
 *   2. Generic code → `errors:generic.<code>`.
 *   3. BE-supplied `message` (English fallback — for codes not yet in the map).
 *   4. The error's own `message` field as the absolute last resort.
 *
 * `params` are forwarded to `t()` as interpolation values, for codes
 * whose catalog message takes one (see `getErrorDetails`). They only
 * apply to the catalog branches — the BE `message` fallback is already a
 * finished sentence.
 */
// Loose-typed t() — keys are built dynamically from BE error codes so
// the declaration-merge narrowing is a hindrance here. Runtime
// missingKeyHandler logs any catalog gap; the BE error-code list is
// the contract.
type LooseT = (key: string, params?: Record<string, unknown>) => string

// `TFunction` (no generics) narrows to the default namespace `'common'`
// via the `CustomTypeOptions` declaration merge, which makes the helper
// reject callers passing `useTranslation('chat').t` etc. Public param
// is `unknown` (then internally cast to LooseT) so any namespace's t
// fits; the keys this helper resolves are FQNs (`errors:domain.X`) that
// work regardless of the bound namespace at runtime.
export const localizeError = (err: Error, t: unknown, params?: Record<string, unknown>): string => {
  const known = localizeKnownError(err, t, params)
  if (known) return known
  const body = err instanceof ApiError ? err.body : undefined
  const envelope = extractEnvelope(body)
  if (envelope?.message) return envelope.message
  return err.message
}

/**
 * Like `localizeError`, but returns `null` instead of falling back to
 * the BE's English `message` when the envelope code has no catalog
 * mapping. For call sites that carry their OWN localized generic
 * fallback (e.g. the comment mutations' "Couldn't post comment") and
 * only want to surface the specific text when a mapped code (such as
 * `richtext_invalid`) arrives — never raw BE dev copy.
 */
export const localizeKnownError = (
  err: Error,
  t: unknown,
  params?: Record<string, unknown>,
): string | null => {
  const domain = localizeDomainError(err, t, params)
  if (domain) return domain
  const tt = t as LooseT
  const envelope = extractEnvelope(err instanceof ApiError ? err.body : undefined)
  const genericKey = envelope?.code ? GENERIC_KEYS[envelope.code] : undefined
  return genericKey ? tt(genericKey, params) : null
}

/**
 * DOMAIN codes only — returns `null` for a generic-taxonomy code
 * (`conflict`, `bad_request`, …) rather than its catalog string.
 *
 * For call sites where the BE's own `message` is MORE specific than the
 * generic catalog copy: a 409 from the status-delete route reads "3 cards
 * are still in this column", which `errors:generic.conflict`'s "That
 * action conflicts with the current state." would throw away. Domain
 * codes are the opposite — they have purpose-written localized copy that
 * should always beat the English wire message.
 *
 * Prefer `localizeError` / `localizeKnownError` unless you specifically
 * want that split.
 */
export const localizeDomainError = (
  err: Error,
  t: unknown,
  params?: Record<string, unknown>,
): string | null => {
  const tt = t as LooseT
  const envelope = extractEnvelope(err instanceof ApiError ? err.body : undefined)
  const code = envelope?.code ?? ownErrorCode(err)
  const domainKey = code ? DOMAIN_KEYS[code] : undefined
  return domainKey ? tt(domainKey, params) : null
}

/**
 * A domain code carried on the error object itself. Some hooks re-throw a
 * BE refusal as a typed error (`WikiDraftStaleError` for a 409
 * `wiki_draft_stale`) so callers can branch on it; those are no longer an
 * `ApiError`, so without this the envelope lookup misses and the raw code
 * string reaches the user as the toast text.
 */
function ownErrorCode(err: Error): string | undefined {
  const code = (err as { code?: unknown }).code
  return typeof code === 'string' ? code : undefined
}

/**
 * Read the BE error envelope's `details` bag off a thrown error.
 *
 * Exists so a call site can pull the domain context a code ships (e.g.
 * `last_status_of_type`'s `columnType` slug) and turn it into the
 * interpolation params its catalog message expects, WITHOUT the generic
 * localization path needing to know about any specific domain. Returns
 * `undefined` for a non-`ApiError` or an envelope with no details.
 */
export const getErrorDetails = (err: Error): BeErrorEnvelope['details'] | undefined => {
  const body = err instanceof ApiError ? err.body : undefined
  return extractEnvelope(body)?.details
}

/**
 * Resolve the localized field-level error messages for a Zod
 * validation 400. Returns a map of `path → message` so a `<FormField>`
 * can pin its `role="alert"` text. Falls back to the generic
 * validation_error key when no field paths are present.
 */
export const localizeFieldErrors = (err: ApiError, t: TFunction): Record<string, string> => {
  const envelope = extractEnvelope(err.body)
  const issues = envelope?.details?.issues ?? []
  if (issues.length === 0) return {}
  const out: Record<string, string> = {}
  for (const issue of issues) {
    const path = issue.path.join('.')
    if (!path) continue
    // Issue codes from Zod are stable strings (`invalid_type`,
    // `too_small`, `invalid_string`, etc.). Map well-known ones to
    // localized keys; fall back to the BE-supplied message for codes
    // we don't have a translation for yet.
    out[path] = localizeZodIssue(issue.code, issue.message, t)
  }
  return out
}

type LooseTWithOptions = (key: string, options?: { defaultValue?: string }) => string

const localizeZodIssue = (code: string, fallbackMessage: string, t: TFunction): string => {
  const tt = t as unknown as LooseTWithOptions
  switch (code) {
    case 'invalid_type':
    case 'invalid_literal':
    case 'invalid_enum_value':
      return tt('validation:invalidValue', { defaultValue: fallbackMessage })
    case 'too_small':
      return tt('validation:tooShort', { defaultValue: fallbackMessage })
    case 'too_big':
      return tt('validation:tooLong', { defaultValue: fallbackMessage })
    case 'invalid_string':
      return tt('validation:invalidFormat', { defaultValue: fallbackMessage })
    default:
      return fallbackMessage
  }
}

const extractEnvelope = (body: unknown): BeErrorEnvelope | null => {
  if (typeof body !== 'object' || body === null) return null
  const obj = body as Record<string, unknown>
  const error = obj.error
  if (typeof error !== 'object' || error === null) return null
  const e = error as Record<string, unknown>
  const code = typeof e.code === 'string' ? e.code : ''
  const message = typeof e.message === 'string' ? e.message : ''
  const details =
    typeof e.details === 'object' && e.details !== null
      ? (e.details as BeErrorEnvelope['details'])
      : undefined
  return { code, message, details }
}
